import React, { useEffect, useState } from 'react';
import { mixpanel } from '@fiverr-private/obs';
import { Dropdown, DropdownAnchor, DropdownContent, DropdownMenu, MenuItem } from '@fiverr-private/dropdown';
import { Text } from '@fiverr-private/typography';
import { Center, Divider, Stack } from '@fiverr-private/layout_components';
import { ProSellerL2Icon, ProServicesL2Icon } from '@fiverr-private/visuals';
import { getProMenusCopiesConfig } from '@fiverr-private/layout_menus';
import { reportBigQueryClick, reportMixpanelClick } from '../../../utils/reporter';
import { getBQData } from '../../../utils/business/bi-utils';
import { buildLinkHref } from '../../../utils/buildLinkHref';
import { HEADER_POPUP_SOURCE } from '../../../constants';
import { TriggerButton } from '../../blocks/TriggerButton';
import { AppI18n } from '../../../i18n';

const itemConfig = {
    offer_services: {
        icon: <ProSellerL2Icon size="md" />,
        bqType: 'i_am_freelancer',
        mpEvent: 'Fiverr logged-out header - Pro - Seller Clicked',
    },
    sourcing: {
        icon: <ProServicesL2Icon size="md" />,
        bqType: 'hire_freelancer',
        mpEvent: 'Fiverr logged-out header - Pro - Buyer Clicked',
    },
} as const;

const isItemConfigKey = (key: string): key is keyof typeof itemConfig => key in itemConfig;

const ProMenuItem = ({ item }: { item: { key: string; text: string; description: string; link: string } }) => {
    const config = isItemConfigKey(item.key) ? itemConfig[item.key] : undefined;
    if (!config) {
        return null;
    }

    const href = buildLinkHref({ link: item.link, source: HEADER_POPUP_SOURCE });

    const handleClick = () => {
        mixpanel.track(config.mpEvent, {});
        reportBigQueryClick(getBQData({ type: config.bqType }));
    };

    return (
        <MenuItem href={href} target="_blank" rel="nofollow" onClick={handleClick}>
            <Stack gap="4" alignItems="flexStart">
                <Center flexShrink="0">{config.icon}</Center>
                <Stack direction="column" gap="0">
                    <Text fontWeight="semibold" color="bodyPrimary">
                        {item.text}
                    </Text>
                    <Text size="b_sm" color="bodyPrimary">
                        {item.description}
                    </Text>
                </Stack>
            </Stack>
        </MenuItem>
    );
};

export const FiverrProNavigation = () => {
    const [isOpen, setIsOpen] = useState(false);
    const { pro_dropdown } = getProMenusCopiesConfig();

    useEffect(() => {
        if (isOpen) {
            reportMixpanelClick({ eventName: 'Fiverr logged-out header - Pro - Clicked' });
        }
    }, [isOpen]);

    const handleOpenChanged = (open: boolean) => setIsOpen(open);

    const handleTriggerClick = () => {
        reportBigQueryClick(getBQData({ type: 'open_menu_fiverr_pro', component: { name: 'Header' } }));
    };

    return (
        <Dropdown position="bottom-end" onOpenChanged={handleOpenChanged}>
            <DropdownAnchor embed>
                <TriggerButton onClick={handleTriggerClick}>
                    <AppI18n k="fiverr_pro_entry.title" />
                </TriggerButton>
            </DropdownAnchor>
            <DropdownContent closeOnClickOutside width="380px">
                <DropdownMenu maxHeight="auto">
                    {pro_dropdown.items.map((item, index) => (
                        <React.Fragment key={item.key}>
                            <ProMenuItem item={item} />
                            {index < pro_dropdown.items.length - 1 && <Divider marginY="2" />}
                        </React.Fragment>
                    ))}
                </DropdownMenu>
            </DropdownContent>
        </Dropdown>
    );
};
